/** Derives the version display data for a feature (see FeatureVersion). */

import type { Feature } from '../../schemas/admin.schemas';

export type VersionMarker = 'retired' | 'end-of-support' | 'unavailable' | null;

export interface FeatureVersionInfo {
  main: string;
  pkg: string | null;
  marker: VersionMarker;
  details: string[];
}

/** Line values arrive as strings or numbers (YAML `22`); compare as trimmed text. */
export function normalizeLine(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const line = String(value).trim();
  return line === '' ? null : line;
}

/** True when `today` is on or after the line's end-of-support date (day granularity). */
export function isEndOfSupport(feature: Feature, line: string, today: Date): boolean {
  const eos = feature.variant_end_of_support?.[line];
  return eos != null && today.toISOString().slice(0, 10) >= eos;
}

export function featureVersionInfo(
  feature: Feature,
  selectedLine?: unknown,
  today: Date = new Date(),
): FeatureVersionInfo {
  const ships = feature.ships ?? [];
  const variants = Object.entries(feature.variant_versions ?? {});
  const retired = Object.entries(feature.retired_variants ?? {});
  const primary = ships.find((s) => s.primary);
  const details: string[] = [
    ...ships.map((s) => `${s.name} ${s.version}`),
    ...variants.map(([option, release]) => `${option} → ${release}`),
    ...retired.map(([option, r]) => `${option} → ${r.release} (retired)`),
    `package ${feature.version}`,
  ];
  const line = normalizeLine(selectedLine) ?? feature.default_variant ?? null;
  const active = line === null ? undefined : feature.variant_versions?.[line];
  const gone = line === null ? undefined : feature.retired_variants?.[line];
  if (line !== null && active !== undefined) {
    const marker = isEndOfSupport(feature, line, today) ? 'end-of-support' : null;
    return { main: active, pkg: feature.version, marker, details };
  }
  if (line !== null && gone !== undefined) {
    return { main: gone.release, pkg: gone.package, marker: 'retired', details };
  }
  if (line !== null && selectedLine !== undefined && normalizeLine(selectedLine) !== null) {
    return { main: line, pkg: feature.version, marker: 'unavailable', details };
  }
  return {
    main: primary?.version ?? feature.version,
    pkg: primary ? feature.version : null,
    marker: null,
    details,
  };
}
